import { Box, Grid, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import AppSelect from "components/StyledComponents/AppSelect"
import AppTextField from "components/StyledComponents/AppTextField"
import DraggableOptions from "../../../../../../components/DraggableOptions"
import DraggableMatchingOptions from "components/DraggableMatchingOptions"
import ImageIcon from "assets/icons/ImageIcon.svg"
import ImageUploadPopup from "../ImageUploadPopup"
import { useDispatch, useSelector } from "react-redux"
import { uploadQuestionImageAction } from "redux/L&D/actions"
import CloseIcon from "assets/icons/closeIcon.svg"
import { learningDevelopmentActions } from "redux/L&D/slice/user"
import { getWeeklyCategoryMenuItemAction } from "redux/marketing/actions"
import FileUploadField from "components/FileUpload/FileUploadField"
import DraggableImageOptions from "components/DraggableImageOptions"

const difficultyLevelChoices = [
  { label: "Beginner", value: "Beginner" },
  { label: "Intermediate", value: "Intermediate" },
  { label: "Advance", value: "Advance" }
]

const questionTypeChoices = [
  { label: "Text", value: "text" },
  { label: "Matching", value: "matching" },
  { label: "2-Images", value: "2-images" },
  { label: "4-Images", value: "4-images" }
]

const QuestionForm = ({
  formik,
  questionIndex,
  questionImageData,
  menuListByIndex,
  onMenuListFetch,
  setMenuListByIndex
}) => {
  const [questionText, setQuestionText] = useState("")
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState()
  const [categoryList, setCategoryList] = useState([])
  const [audioFile, setAudioFile] = useState(null)
  const [originalOptionsBackup, setOriginalOptionsBackup] = useState(null)
  const [previousQuestionType, setPreviousQuestionType] = useState(null)
  const [lastCorrectIndex, setLastCorrectIndex] = useState(null)
  const dispatch = useDispatch()
  const { weeklyCategoryList } = useSelector((store) => store?.marketing)
  const questionMenuList = menuListByIndex?.[questionIndex]

  const handleAudioChange = (file) => {
    setAudioFile(file)
  }

  const handleAudioDelete = () => {
    setAudioFile(null)
  }

  const onImagePopupOpen = () => {
    setIsImagePopupOpen(true)
  }

  const onImagePopupClose = () => {
    setIsImagePopupOpen(false)
  }

  const questionErrors =
    (formik.errors.questionSet &&
      formik.errors.questionSet.length &&
      formik.errors.questionSet[questionIndex]) ||
    {}
  const questionTouched =
    (formik.touched.questionSet &&
      formik.touched.questionSet.length &&
      formik.touched.questionSet[questionIndex]) ||
    {}

  const getOptionValues = (values = formik.values) => {
    return values?.questionSet?.[questionIndex]?.optionSet
  }

  const getQuestionType = (values = formik.values) => {
    return values?.questionSet?.[questionIndex]?.questionType
  }

  const getOptionPath = () => {
    return `questionSet[${questionIndex}].optionSet`
  }

  const onQuestionTextChange = (e) => {
    setQuestionText(e.target.value)
    const timeOutId = setTimeout(
      () => formik.setFieldValue(`questionSet[${questionIndex}].question`, e.target.value),
      800
    )
    return () => clearTimeout(timeOutId)
  }

  const onImageUpload = (newImage) => {
    let data = new FormData()
    data.append("media", newImage)
    data.append("media_type", "IMAGE")
    data.append("content_type", newImage.type)

    dispatch(
      uploadQuestionImageAction({
        index: questionIndex,
        data: data
      })
    )
  }

  const onImageDelete = () => {
    setSelectedImage(null)
    dispatch(
      learningDevelopmentActions.setUploadedImages({
        index: questionIndex,
        data: null
      })
    )
  }

  const onCategorySelect = (e) => {
    formik.setFieldValue(`questionSet[${questionIndex}].category`, e.value)
    formik.setFieldValue(`questionSet[${questionIndex}].categoryName`, e.label)
    if (e.label === "Wine Region") {
      formik.setFieldValue(`questionSet[${questionIndex}].filterCategory`, "Wine Region")
    } else {
      formik.setFieldValue(`questionSet[${questionIndex}].filterCategory`, "")
      formik.setFieldValue(`questionSet[${questionIndex}].relatedFood`, "")
    }

    setMenuListByIndex((prev) => {
      const updated = { ...prev }
      delete updated[questionIndex]
      return updated
    })
  }

  const onFilterCategorySelect = (e) => {
    formik.setFieldValue(`questionSet[${questionIndex}].filterCategory`, e.value)
    formik.setFieldValue(`questionSet[${questionIndex}].relatedFood`, "")

    setMenuListByIndex((prev) => {
      const updated = { ...prev }
      delete updated[questionIndex]
      return updated
    })
  }

  const onRelatedFoodSelect = (e) => {
    formik.setFieldValue(`questionSet[${questionIndex}].relatedFood`, e.value)
  }

  const ensureSingleCorrectForText = (options = []) => {
    if (!options.length) return options

    const validIndex =
      lastCorrectIndex !== null && lastCorrectIndex >= 0 && lastCorrectIndex < options.length
        ? lastCorrectIndex
        : options.findIndex((option) => option?.isCorrect)

    const correctIndex = validIndex !== -1 ? validIndex : 0

    return options.map((option, idx) => ({
      ...option,
      isCorrect: idx === correctIndex
    }))
  }

  const handleQuestionTypeChange = (e) => {
    const newQuestionType = e.value
    const currentQuestionType =
      formik.values?.questionSet?.[questionIndex]?.questionType || previousQuestionType
    const currentOptions = formik.values?.questionSet?.[questionIndex]?.optionSet || []
    const currentCorrectIndex = currentOptions.findIndex((option) => option?.isCorrect)

    if (currentCorrectIndex !== -1) {
      setLastCorrectIndex(currentCorrectIndex)
    }

    // Store original options before switching to 2-images
    if (newQuestionType === "2-images" && currentQuestionType !== "2-images") {
      // Backup original options if we have more than 2 and they haven't been backed up yet
      if (currentOptions.length > 2 && !originalOptionsBackup) {
        setOriginalOptionsBackup([...currentOptions])
      }
    }

    // Restore original options when switching back from 2-images to other types
    if (
      currentQuestionType === "2-images" &&
      newQuestionType !== "2-images" &&
      originalOptionsBackup &&
      originalOptionsBackup.length > 2
    ) {
      const restoredOptions = originalOptionsBackup?.map((option) => {
        if (newQuestionType === "matching") {
          return {
            ...option,
            answer: option.answer || "",
            name: option.name || ""
          }
        }
        return {
          ...option,
          name: option.name || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }
      })
      const normalizedRestoredOptions =
        newQuestionType === "text" ? ensureSingleCorrectForText(restoredOptions) : restoredOptions
      formik.setFieldValue(`questionSet[${questionIndex}].optionSet`, normalizedRestoredOptions)
      setOriginalOptionsBackup(null)
    } else {
      if (newQuestionType === "matching") {
        const updatedOptions =
          currentOptions?.length > 0
            ? currentOptions?.map((option) => ({
                ...option,
                answer: option.answer || "",
                name: option.name || ""
              }))
            : [{ name: "", answer: "" }]
        formik.setFieldValue(`questionSet[${questionIndex}].optionSet`, updatedOptions)
      } else if (currentOptions.length > 0) {
        const updatedOptions = currentOptions.map((option) => ({
          ...option,
          name: option.name || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }))
        const normalizedUpdatedOptions =
          newQuestionType === "text" ? ensureSingleCorrectForText(updatedOptions) : updatedOptions
        formik.setFieldValue(`questionSet[${questionIndex}].optionSet`, normalizedUpdatedOptions)
      }
    }

    setPreviousQuestionType(currentQuestionType)
    formik.setFieldValue(`questionSet[${questionIndex}].questionType`, newQuestionType)

    // Clear validation errors and touched state for optionSet when switching types
    // This prevents errors from one question type persisting when switching to another
    if (
      formik.errors?.questionSet?.[questionIndex]?.optionSet ||
      formik.touched?.questionSet?.[questionIndex]?.optionSet
    ) {
      const errors = { ...formik.errors }
      const touched = { ...formik.touched }

      if (errors.questionSet?.[questionIndex]?.optionSet) {
        errors.questionSet = [...(errors.questionSet || [])]
        if (errors.questionSet[questionIndex]) {
          const questionErrors = { ...errors.questionSet[questionIndex] }
          delete questionErrors.optionSet
          errors.questionSet[questionIndex] = questionErrors
        }
        formik.setErrors(errors)
      }

      if (touched.questionSet?.[questionIndex]?.optionSet) {
        touched.questionSet = [...(touched.questionSet || [])]
        if (touched.questionSet[questionIndex]) {
          const questionTouched = { ...touched.questionSet[questionIndex] }
          delete questionTouched.optionSet
          touched.questionSet[questionIndex] = questionTouched
        }
        formik.setTouched(touched)
      }
    }
  }

  useEffect(() => {
    setQuestionText(formik.values?.questionSet?.[questionIndex]?.question ?? "")
    const initialQuestionType = formik.values?.questionSet?.[questionIndex]?.questionType
    const initialCorrectIndex =
      formik.values?.questionSet?.[questionIndex]?.optionSet?.findIndex((o) => o?.isCorrect) ?? -1
    if (initialQuestionType) {
      setPreviousQuestionType(initialQuestionType)
    }
    if (initialCorrectIndex !== -1) {
      setLastCorrectIndex(initialCorrectIndex)
    }
  }, [])

  // Reset backup state when question type changes externally (e.g., when a new question is added)
  useEffect(() => {
    const currentQuestionType = formik.values?.questionSet?.[questionIndex]?.questionType
    // If question type is reset or changed externally, reset backup state
    if (
      currentQuestionType &&
      previousQuestionType &&
      currentQuestionType !== previousQuestionType
    ) {
      // Only reset if it wasn't changed through our handler (which already handles this)
      // This handles cases where question is reset or reinitialized
      if (currentQuestionType === "text" && !previousQuestionType) {
        setOriginalOptionsBackup(null)
      }
    }
  }, [formik.values?.questionSet?.[questionIndex]?.questionType])

  useEffect(() => {
    if (onMenuListFetch) {
      onMenuListFetch(questionIndex)
    }
  }, [questionIndex])

  useEffect(() => {
    const weeklyCategoryId = formik?.values?.questionSet?.[questionIndex]?.category
    const menuCategoryId = formik?.values?.questionSet?.[questionIndex]?.filterCategory
    if (weeklyCategoryId && menuCategoryId) {
      if (onMenuListFetch) {
        onMenuListFetch(questionIndex)
      }
      dispatch(
        getWeeklyCategoryMenuItemAction({
          params:
            menuCategoryId === "Wine Region"
              ? {
                  menu_category_id: menuCategoryId
                }
              : {
                  weekly_category_id: weeklyCategoryId,
                  menu_category_id: menuCategoryId
                }
        })
      )
    }
  }, [
    formik?.values?.questionSet?.[questionIndex]?.category,
    formik?.values?.questionSet?.[questionIndex]?.filterCategory,
    questionIndex
  ])

  function getMenuCategory(name) {
    const category = categoryList?.length > 0 && categoryList?.find((cat) => cat.label === name)

    if (!category || !category.subcategories) return []

    const uniqueLabels = Array.from(new Set(category.subcategories.map((sub) => sub.label)))

    return uniqueLabels.map((label) => {
      const subcategory = category.subcategories.find((sub) => sub.label === label)
      return {
        label,
        value: subcategory?.value || label
      }
    })
  }

  useEffect(() => {
    if (weeklyCategoryList?.menu?.length === 0) return
    const newList = weeklyCategoryList?.menu?.map((cat) => {
      return {
        value: cat.category_id,
        label: cat.category_name,
        subcategories: (cat.items || cat.subcategories)?.map((sub) => {
          const label = sub.meal_period_name || sub.category_name || sub.subcategory_name
          const value = sub.meal_period_id || sub.category_id || sub.subcategory_id

          return { value, label }
        })
      }
    })
    setCategoryList(newList)
  }, [weeklyCategoryList])

  return (
    <React.Fragment>
      <ImageUploadPopup
        {...{ setSelectedImage, selectedImage, onImageDelete, onImageUpload }}
        open={isImagePopupOpen}
        onClose={onImagePopupClose}
        aspectHeight={1}
        aspectWidth={1}
      />
      <Box sx={{ padding: "0 11px" }}>
        <Grid container spacing={4}>
          <Grid item lg={4} sm={6}>
            <AppSelect
              id={`questionSet[${questionIndex}].category`}
              name={`questionSet[${questionIndex}].category`}
              menuItems={categoryList ?? []}
              label="Weekly Challenge Category"
              isRequired={true}
              value={formik?.values?.questionSet?.[questionIndex]?.category}
              onMenuClick={onCategorySelect}
              error={questionTouched.category && questionErrors.category}
              touched={questionTouched.category}
            />
          </Grid>
          <Grid item lg={4} sm={6}>
            <AppSelect
              id={`questionSet[${questionIndex}].filterCategory`}
              name={`questionSet[${questionIndex}].filterCategory`}
              menuItems={getMenuCategory(
                formik?.values?.questionSet?.[questionIndex]?.categoryName
              )}
              label="Menu Category"
              isRequired={true}
              value={formik?.values?.questionSet?.[questionIndex]?.filterCategory}
              onMenuClick={onFilterCategorySelect}
              error={questionTouched.filterCategory && questionErrors.filterCategory}
              touched={questionTouched.filterCategory}
            />
          </Grid>
          <Grid item lg={4} sm={6}>
            <AppSelect
              id={`questionSet[${questionIndex}].relatedFood`}
              name={`questionSet[${questionIndex}].relatedFood`}
              menuItems={questionMenuList || []}
              label="Menu Item"
              isRequired={true}
              value={formik?.values?.questionSet?.[questionIndex]?.relatedFood}
              onMenuClick={onRelatedFoodSelect}
              error={questionTouched.relatedFood && questionErrors.relatedFood}
              touched={questionTouched.relatedFood}
            />
          </Grid>
        </Grid>
        <Grid container mt={0} spacing={4}>
          <Grid item lg={4} sm={6}>
            <AppSelect
              id={`questionSet[${questionIndex}].difficultyLevel`}
              name={`questionSet[${questionIndex}].difficultyLevel`}
              menuItems={difficultyLevelChoices}
              label="Difficulty Level"
              isRequired={true}
              value={formik?.values?.questionSet?.[questionIndex]?.difficultyLevel}
              onChange={formik.handleChange}
              error={questionTouched.difficultyLevel && questionErrors.difficultyLevel}
              touched={questionTouched.difficultyLevel}
            />
          </Grid>
          <Grid item lg={4} sm={6}>
            <AppSelect
              id={`questionSet[${questionIndex}].questionType`}
              name={`questionSet[${questionIndex}].questionType`}
              menuItems={questionTypeChoices}
              label="Question Type"
              isRequired={true}
              value={formik?.values?.questionSet?.[questionIndex]?.questionType}
              onMenuClick={handleQuestionTypeChange}
              error={questionTouched.questionType && questionErrors.questionType}
              touched={questionTouched.questionType}
            />
          </Grid>
        </Grid>
        <Box height={35} />
        <Typography variant="h2" sx={{ fontSize: 20 }}>
          Question
        </Typography>
        <Box height={19} />
        <Box className="d-flex align-items-start mb-2">
          <AppTextField
            placeholder="Enter Question"
            key={`questionSet[${questionIndex}].question`}
            value={questionText}
            onChange={onQuestionTextChange}
            className="flex-grow-1 flex-shrink-1"
            fullWidth
            error={questionTouched.question && Boolean(questionErrors.question)}
            helperText={questionTouched.question && questionErrors.question}
          />
          <img
            role="button"
            src={ImageIcon}
            onClick={onImagePopupOpen}
            className="flex-shrink-0 ms-4"
            alt="Upload Image"
          />
        </Box>
        <Box height={19} />
        {formik?.values?.questionSet?.[questionIndex]?.questionType === "soundbite" && (
          <Box>
            <FileUploadField
              label="Upload Audio File"
              isRequired={true}
              isVariant={true}
              value={audioFile}
              placeholder="Upload MP3 File"
              allowedTypes={["audio/mpeg", "audio/wav", "audio/mp3"]}
              maxFileSizeInBytes={10 * 1024 * 1024}
              onChange={handleAudioChange}
              onDelete={handleAudioDelete}
            />
          </Box>
        )}
        {questionImageData?.url ? (
          <Box className="mt-4 position-relative" sx={{ width: "fit-content" }}>
            <img style={{ height: 182, borderRadius: 6 }} src={questionImageData.url} alt="" />
            <img
              role="button"
              src={CloseIcon}
              alt=""
              className="position-absolute"
              style={{ top: 12, right: 12 }}
              onClick={onImageDelete}
            />
          </Box>
        ) : null}
        {!!formik?.values?.questionSet?.[questionIndex]?.questionType &&
        formik?.values?.questionSet?.[questionIndex]?.questionType === "matching" ? (
          <DraggableMatchingOptions {...{ formik, getOptionValues, getOptionPath }} />
        ) : !!formik?.values?.questionSet?.[questionIndex]?.questionType &&
          formik?.values?.questionSet?.[questionIndex]?.questionType !== "text" &&
          formik?.values?.questionSet?.[questionIndex]?.questionType !== "soundbite" ? (
          <DraggableImageOptions {...{ formik, getOptionValues, getQuestionType, getOptionPath }} />
        ) : (
          <DraggableOptions {...{ formik, getOptionValues, getOptionPath }} />
        )}
      </Box>
    </React.Fragment>
  )
}

export default QuestionForm
